import React, { useState } from 'react';
import { Button } from './ui/button';
import { Brain, BarChart3, Cog, Code2, ArrowRight, CheckCircle } from 'lucide-react';
import { ServiceContactModal } from './ServiceContactModal';

const services = [
  {
    icon: Brain,
    title: "Inteligencia Artificial",
    description: "Automatiza decisiones y procesos con IA personalizada para tu negocio.",
    features: ["Chatbots inteligentes", "Prediccion de datos", "Automatizacion con IA"],
    highlight: "Lo mas solicitado",
    number: "01"
  },
  {
    icon: BarChart3,
    title: "Analisis de Datos",
    description: "Transforma tus datos en decisiones estrategicas con dashboards claros.",
    features: ["Dashboards interactivos", "Reportes automaticos", "Visualizacion de KPIs"],
    highlight: null,
    number: "02"
  },
  {
    icon: Cog,
    title: "Automatizacion",
    description: "Elimina tareas repetitivas y ahorra horas de trabajo cada semana.",
    features: ["Integracion de sistemas", "Workflows automaticos", "Procesos sin errores"],
    highlight: null,
    number: "03"
  },
  {
    icon: Code2,
    title: "Software a Medida",
    description: "Aplicaciones web y moviles disenadas especificamente para tu empresa.",
    features: ["Apps web modernas", "APIs robustas", "Sistemas de gestion"],
    highlight: null,
    number: "04"
  }
];

export const Services = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');

  const handleServiceClick = (serviceTitle) => {
    if (window.innerWidth < 768) {
      document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      setSelectedService(serviceTitle);
      setModalOpen(true);
    }
  };

  return (
    <section id="servicios" className="py-24 lg:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <span className="text-sm font-medium text-accent uppercase tracking-[0.2em] mb-4 block">
              Servicios
            </span>
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-foreground leading-tight text-balance">
              Lo que podemos hacer por ti
            </h2>
          </div>
          <p className="text-muted-foreground text-lg max-w-md leading-relaxed">
            Nos especializamos en 4 areas clave para impulsar tu negocio con tecnologia de vanguardia.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <div
                key={index}
                className="group relative bg-card border border-border rounded-2xl p-8 lg:p-10 transition-all duration-500 hover:border-foreground/20 hover:shadow-lg"
              >
                {/* Highlight Badge */}
                {service.highlight && (
                  <span className="absolute -top-3 left-8 px-4 py-1 bg-accent text-accent-foreground text-xs font-semibold rounded-full tracking-wide">
                    {service.highlight}
                  </span>
                )}

                {/* Number */}
                <span className="text-sm font-medium text-muted-foreground/40 mb-6 block">
                  {service.number}
                </span>

                {/* Icon & Title */}
                <div className="flex items-start gap-5 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-foreground/5 flex items-center justify-center flex-shrink-0 group-hover:bg-accent/10 transition-colors duration-300">
                    <IconComponent className="h-6 w-6 text-foreground group-hover:text-accent transition-colors duration-300" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-foreground mb-2">
                      {service.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8 pl-[76px]">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-sm text-foreground/70">
                      <CheckCircle className="h-4 w-4 text-accent flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <div className="pl-[76px]">
                  <Button
                    onClick={() => handleServiceClick(service.title)}
                    variant="outline"
                    className="border-foreground/15 text-foreground hover:bg-foreground hover:text-background rounded-full px-6 transition-all duration-300 group/btn"
                  >
                    Quiero saber mas
                    <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform duration-300" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <ServiceContactModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        serviceName={selectedService}
      />
    </section>
  );
};
